/**
 * Inspector for the **Undo** node — the most destructive step the platform has.
 *
 * Everything here is shaped by that. The picker only offers loads that can
 * actually be taken back (and says why the others cannot); the default mode is
 * the one that keeps the records; the reason is mandatory because the audit row
 * is all that will be left; and the node says, before the run, whether an
 * approval will be required.
 */

import { useEffect, useState } from "react";
import { Alert, Badge, Group, Loader, Radio, Select, Stack, Text, Textarea } from "@mantine/core";

import { useAccessToken } from "@/features/auth/useAccessToken";
import { listLoadReports, type LoadReportOut } from "@/shared/api/loadReports";
import { toastApiError } from "@/shared/ui/toast";

/** Mirrors the engine: these states always gate an undo behind an approval. */
const GATED = ["validation", "production"];

function describe(report: LoadReportOut): string {
  const when = new Date(report.loaded_at_utc).toLocaleString();
  return `${report.object_name} · ${report.created_count} created · ${report.pipeline_name ?? "pipeline"} · ${when}`;
}

export function UndoConfig({
  config,
  editable,
  onChange,
  lifecycleState,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
  lifecycleState: string;
}) {
  const getToken = useAccessToken();
  const [reports, setReports] = useState<LoadReportOut[] | null>(null);
  const reportId = String(config.reportId ?? "");
  const mode = String(config.mode ?? "inactivate");
  const gated = GATED.includes(lifecycleState);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const items = (await listLoadReports(await getToken())).items;
        if (!cancelled) setReports(items);
      } catch (err) {
        toastApiError("Could not load the list of loads", err);
        if (!cancelled) setReports([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  const chosen = (reports ?? []).find((r) => r.report_id === reportId);

  return (
    <Stack gap="sm">
      <Alert color="red" variant="light" title="This removes data from a connected system">
        <Text size="sm">
          Undo takes back <b>one recorded load</b> — the exact records it created, by id. It never
          runs a query, never touches records it only <i>updated</i> (reversing those would need a
          before-image the platform does not keep), and a load can be undone <b>once</b>.
        </Text>
      </Alert>

      {reports === null ? (
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="sm" c="dimmed">
            Finding loads that can be taken back…
          </Text>
        </Group>
      ) : reports.length === 0 ? (
        <Alert color="gray" variant="light">
          Nothing to undo. A load appears here once a run has created records and nothing has taken
          it back yet.
        </Alert>
      ) : (
        <Select
          label="Load to take back"
          description="Newest first. Only loads that created something and have not been undone."
          placeholder="Pick a load"
          data={reports.map((r) => ({ value: r.report_id, label: describe(r) }))}
          value={reportId || null}
          onChange={(v) => onChange("reportId", v ?? "")}
          disabled={!editable}
          searchable
        />
      )}

      {chosen && (
        <Alert color="gray" variant="light">
          <Text size="sm">
            <b>{chosen.created_count}</b> {chosen.object_name} records, loaded by run{" "}
            <code>{chosen.run_id}</code>.
            {chosen.updated_count > 0 && (
              <>
                {" "}
                Its other <b>{chosen.updated_count}</b> updated records are <b>not</b> affected.
              </>
            )}
          </Text>
        </Alert>
      )}

      <Radio.Group label="How" value={mode} onChange={(v) => onChange("mode", v)}>
        <Stack gap={6} mt={6}>
          <Radio
            value="inactivate"
            disabled={!editable}
            label="Inactivate (recommended)"
            description="Sets each record inactive. The record, its audit trail and anything referencing it all survive — usually what a regulated customer means by taking a load back."
          />
          <Radio
            value="delete"
            disabled={!editable}
            label="Delete"
            description="Removes the records from the vault. A record something else now references will refuse, and that refusal is reported per row."
          />
        </Stack>
      </Radio.Group>

      <Textarea
        label="Reason"
        description="Required. The audit row is the only record of why this data went."
        placeholder="Wave 2 was loaded against the wrong vault."
        value={String(config.reason ?? "")}
        onChange={(e) => onChange("reason", e.currentTarget.value)}
        disabled={!editable}
        autosize
        minRows={2}
        maxLength={500}
      />

      <Alert color={gated ? "orange" : "blue"} variant="light" title="Approval">
        {gated ? (
          <Text size="sm">
            This pipeline is in <b>{lifecycleState}</b>, so the run will{" "}
            <b>stop for an Administrator&apos;s approval</b> before anything is touched — and not
            the person who started it. That gate cannot be switched off here.
          </Text>
        ) : (
          <Text size="sm">
            In <b>development</b> the undo runs straight away. Promote the pipeline and it will stop
            for an Administrator&apos;s approval first.
          </Text>
        )}
      </Alert>

      <Group gap="xs">
        <Badge size="xs" color="red" variant="light">
          audited
        </Badge>
        <Text size="xs" c="dimmed">
          Every undo records the ids, both run ids and your reason.
        </Text>
      </Group>
    </Stack>
  );
}
